import React, { useCallback, useState } from 'react';
import { MessagesSquare, CheckCircle2, XCircle, Send } from 'lucide-react';
import SectionShell from '../plan/SectionShell';

/**
 * ThreeWayCommLog — sender/receiver/message + verified flag.
 *
 * Every send posts a `three_way_comm` timeline event. The verified flag
 * signals whether the receiver correctly repeated the message back. Not
 * editable after logging (audit-log integrity).
 */
export default function ThreeWayCommLog({ performState, ownerEmail, onLogged, isReadOnly }) {
  const comms = performState?.three_way_comms || [];
  const [sender, setSender] = useState(ownerEmail || '');
  const [receiver, setReceiver] = useState('');
  const [message, setMessage] = useState('');
  const [verified, setVerified] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    if (!sender.trim() || !receiver.trim() || !message.trim()) return;
    setBusy(true);
    try {
      await onLogged?.('three_way_comm', {
        sender:   sender.trim(),
        receiver: receiver.trim(),
        message:  message.trim(),
        verified,
      });
      // Preserve sender name for repeated logging; clear the rest.
      setReceiver('');
      setMessage('');
      setVerified(true);
    } finally {
      setBusy(false);
    }
  }, [sender, receiver, message, verified, onLogged]);

  return (
    <SectionShell
      icon={MessagesSquare}
      title="Three-Way Communication Log"
      subtitle="Every switching order, hand-off, and critical instruction should be repeated back and confirmed."
      color="#a78bfa"
      badge={comms.length ? `${comms.length} logged` : 'Log every hand-off'}
      testId="jbv2-section-three-way"
    >
      {!isReadOnly && (
        <div className="jbv2-add-row" data-testid="jbv2-3way-form" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="Sender"
              data-testid="jbv2-3way-sender"
              aria-label="Sender"
              style={{ flex: 1 }}
            />
            <input
              type="text"
              value={receiver}
              onChange={(e) => setReceiver(e.target.value)}
              placeholder="Receiver"
              data-testid="jbv2-3way-receiver"
              aria-label="Receiver"
              style={{ flex: 1 }}
            />
          </div>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Message — the exact instruction being sent"
            data-testid="jbv2-3way-message"
            aria-label="Message"
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className={`jbv2-field-check${verified ? ' jbv2-field-check--on' : ''}`} data-testid="jbv2-3way-verified-check">
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
                data-testid="jbv2-3way-verified-input"
              />
              <span>Receiver repeated back correctly</span>
            </label>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !sender.trim() || !receiver.trim() || !message.trim()}
              data-testid="jbv2-3way-log-btn"
            >
              <Send size={13} /> Log communication
            </button>
          </div>
        </div>
      )}

      {comms.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-3way-empty">
          No three-way communications logged yet. Log the first hand-off above.
        </div>
      )}
      {comms.slice().reverse().map((c, i) => (
        <div
          key={c.id || i}
          className="jbv2-row"
          data-testid={`jbv2-3way-row-${i}`}
        >
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat">
              {c.sender} <ChevronArrow /> {c.receiver}
            </div>
            <div className="jbv2-row-note">&ldquo;{c.message}&rdquo;</div>
            <div className="jbv2-hp-row-meta">
              {new Date(c.created_at).toLocaleTimeString()}
              {c.actor && <> · logged by {c.actor}</>}
            </div>
          </div>
          <div className="jbv2-row-actions">
            {c.verified ? (
              <span className="jbv2-hp-row-cite" data-testid={`jbv2-3way-verified-${i}`}>
                <CheckCircle2 size={11} /> Verified
              </span>
            ) : (
              <span
                className="jbv2-hp-row-cite"
                style={{ background: 'rgba(220,38,38,0.10)', borderColor: 'rgba(220,38,38,0.45)', color: '#fca5a5' }}
                data-testid={`jbv2-3way-unverified-${i}`}
              >
                <XCircle size={11} /> Not verified
              </span>
            )}
          </div>
        </div>
      ))}
    </SectionShell>
  );
}

/** Small inline arrow — kept local so the caller doesn't grow imports. */
function ChevronArrow() {
  return (
    <span aria-hidden="true" style={{ color: '#67e8f9', margin: '0 4px' }}>
      →
    </span>
  );
}
